import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { CodeBlock } from '../components/CodeBlock';

interface ModuleIntroProps {
  title: string;
  subtitle: string;
  description: string;
  icon: LucideIcon;
  color: string;
  comingSoon?: boolean;
}

export const ModuleIntro: React.FC<ModuleIntroProps> = ({ 
  title, subtitle, description, color, comingSoon = false 
}) => {
  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="space-y-6">
        <div 
          className="inline-flex items-center gap-2.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.2em] shadow-sm border border-slate-100"
          style={{ backgroundColor: `${color}10`, color: color }}
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: color }}></span>
            <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: color }}></span>
          </span>
          {comingSoon ? 'Work in Progress' : 'Active Module'}
        </div>
        
        <div className="space-y-4">
          <h1 className="text-5xl sm:text-7xl font-black tracking-tighter text-slate-900 leading-[0.9]">
            {title}<span className="text-primary">.</span>
          </h1>
          <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-slate-500 to-slate-800 bg-clip-text text-transparent">
            {subtitle}
          </p>
        </div>
      </div>

      <div className="prose prose-slate prose-lg max-w-none">
         <p className="text-slate-600 leading-relaxed font-medium text-lg lg:text-xl">{description}</p>
      </div>

      {comingSoon ? (
        <div 
          className="p-10 rounded-[2.5rem] space-y-8 border mt-16 bg-white shadow-2xl shadow-slate-200/60 relative overflow-hidden group"
          style={{ borderColor: `${color}20` }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-slate-50 to-transparent opacity-50 -z-10" />
          
          <div className="space-y-3 relative z-10">
            <h4 className="text-2xl font-black tracking-tight text-slate-900">Development Roadmap</h4>
            <p className="text-slate-500 leading-relaxed font-medium text-lg">
              The documentation and active monitoring logic for this module are currently being migrated to the unified Cyxor Hub. 
              Real-time policy configuration, agent installation guides, and integration APIs will appear here soon.
            </p>
          </div>

          <div className="space-y-5 relative z-10">
            <div className="flex justify-between items-end mb-2">
               <span className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Migration Status</span>
               <span className="text-lg font-mono font-black" style={{ color: color }}>65%</span>
            </div>
            <div className="h-5 w-full rounded-2xl bg-slate-100 p-1 border border-slate-200/50 shadow-inner overflow-hidden">
               <div className="h-full rounded-xl shadow-[0_0_15px_rgba(255,107,0,0.3)] relative transition-all duration-1000 ease-out" style={{ backgroundColor: color, width: '65%' }}>
                 <div className="absolute inset-0 bg-white/20 animate-pulse" />
                 <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
               </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
               <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-slate-50 border border-slate-100 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]" />
                  API Core Complete
               </div>
               <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-slate-50 border border-slate-100 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  <div className="w-2 h-2 rounded-full bg-slate-300" />
                  UI Integration Pending
               </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-12 space-y-6">
           <h3 className="text-2xl font-black text-slate-900 tracking-tight">Quick Integration</h3>
           <CodeBlock 
             language="bash" 
             code={`# Example implementation command for ${title.toLowerCase()}\nnpx @cyxor/${title.toLowerCase()} init`} 
             filename="Terminal"
           />
        </div>
      )}
    </div>
  );
};
